import Button from '../components/button/button'
import EditFavoritesButton from '../components/button/EditFavoritesButton'
import Message from '../components/message/message'
import { PaginateBlock } from '../components/PaginateBlock/PaginateBlock'
import TvBannerMessage from '../components/TvCards/TvBannerMessage'
import TvEntityCard from '../components/TvCards/TvEntityCard'
import TvEventCard from '../components/TvCards/TvEventCard'
import TvFavoriteCard from '../components/TvCards/TvFavoriteCard'
import TvNavigationCard from '../components/TvCards/TvNavigationCard'
import TvOriginalCard from '../components/TvCards/TvOriginalCard'
import TvPaginationCard from '../components/TvCards/TvPaginationCard'
import TvVideoCard from '../components/TvCards/TvVideoCard'
import TvCarouselContainer from '../components/TvCarouselContainer/TvCarouselContainer'
import { LEFT_SAFE_ZONE_MARGIN, PAGINATION_BLOCK_HEIGHT } from '../constants/general'
import {
  LinkModelStyle,
  LinkModelStylesEnum,
  LinkModelType,
  LinkModelTypesEnum,
  MessageModelStyle,
  MessageModelStylesEnum,
  MessageModelType,
  MessageModelTypesEnum,
  TvButtonStyle,
  TvButtonStylesEnum,
  TvCardStyle,
  TvCardStylesEnum,
  TvCardType,
  TvCardTypesEnum,
} from '../types/flo-enums'
import {
  MessageModel,
  TvCarouselContainerModel,
  TvRowsContainerModelItem,
} from '../types/flo-experience'

export const CARD_SPACING = 32
export const CAROUSEL_MARGIN_BOTTOM = 80
export const PAGINATION_OFFSET = CAROUSEL_MARGIN_BOTTOM * 2

export const toCarouselRef = (i) => `Carousel-${i}`
export const toCardRef = (i) => `Card-${i}`

export const toContainer = (
  container: TvRowsContainerModelItem,
  index: number,
  lastIndex?: number,
) => {
  const type = container?.type
  const style = container?.style
  const rowIndex = lastIndex ? lastIndex + index : index

  if (type === MessageModelTypesEnum.Message) {
    return toMessage(
      container,
      0,
      rowIndex,
      getMessageSize((container as MessageModel).style)?.h,
      container.style,
      true,
    )
  } else if (
    type === LinkModelTypesEnum.Link &&
    (style === LinkModelStylesEnum.Paginate || style === LinkModelStylesEnum.Open)
  ) {
    return toPaginateBlock(container, rowIndex)
  } else {
    return toCarousel(container as TvCarouselContainerModel, rowIndex)
  }
}

export const toPaginateBlock = (container: TvRowsContainerModelItem, index?: number) => {
  return {
    w: (w) => w - LEFT_SAFE_ZONE_MARGIN * 2,
    h: PAGINATION_BLOCK_HEIGHT,
    type: PaginateBlock,
    data: container,
    ref: toCarouselRef(index ?? 0),
  }
}

export const toCarousel = (carousel: TvCarouselContainerModel, index?: number) => {
  const carouselRef = toCarouselRef(index ?? 0)
  const cardType = carousel?.items?.[0]?.type as TvCardType
  const cardStyle = carousel?.items?.[0]?.style
  const cardSize = getCardSize(cardType, cardStyle)
  const items = carousel?.items ?? []

  const carouselContainer = {
    type: TvCarouselContainer,
    ref: carouselRef,
    items: items,
    title: carousel?.title?.text,
    carouselIndex: index ?? 0,
    carouselType: cardType,
    cardWidth: cardSize?.w,
    cardHeight: cardSize?.h,
    cardStyle: cardStyle,
    data: carousel,
    flexItem: {
      marginBottom: CAROUSEL_MARGIN_BOTTOM,
    },
  }

  return carouselContainer as unknown as TvCarouselContainer
}

export const getCardSize = (
  cardType: TvCardType = TvCardTypesEnum.TvEventCard,
  style: string = TvCardStylesEnum.Standard,
) => {
  const cardSizes = {
    [TvCardTypesEnum.TvEventCard]: eventCardSize,
    [TvCardTypesEnum.TvVideoCard]: videoCardSize,
    [TvCardTypesEnum.TvNavigationCard]: navigationCardSize,
    [TvCardTypesEnum.TvOriginalCard]: originalCardSize,
    [TvCardTypesEnum.TvEntityCard]: entityCardSize,
    [TvCardTypesEnum.TvFavoriteCard]: favoriteCardSize,
    [TvCardTypesEnum.TvButton]: carouselButtonSize,
    [TvCardTypesEnum.TvBannerMessage]: bannerCardSize,
  }

  return cardSizes[cardType]?.[style]
}

export const toCard = (data, index, rowIndex, h, w, style, totalCards = 0) => {
  const type = data?.type

  switch (type) {
    case TvCardTypesEnum.TvEventCard:
      return createEventCard(data, index, rowIndex, h, w, style, type, totalCards)
    case TvCardTypesEnum.TvVideoCard:
      return createVideoCard(data, index, rowIndex, h, w, style, type, totalCards)
    case TvCardTypesEnum.TvNavigationCard:
      return createNavigationCard(data, index, rowIndex, h, w, style, type, totalCards)
    case TvCardTypesEnum.TvOriginalCard:
      return createOriginalCard(data, index, rowIndex, h, w, style, type, totalCards)
    case TvCardTypesEnum.TvEntityCard:
      return createEntityCard(data, index, rowIndex, h, w, style, type, totalCards)
    case TvCardTypesEnum.TvFavoriteCard:
      return createFavoriteCard(data, index, rowIndex, style, type, totalCards)
    case TvCardTypesEnum.TvButton:
      return createButton(data, index, rowIndex, totalCards)
    case TvCardTypesEnum.TvPaginationCard:
      return createPaginationCard(data, index, rowIndex, h, w, totalCards)
    case TvCardTypesEnum.TvBannerMessage:
      return createBannerMessage(data, index, rowIndex, h, w)
    default:
      return createEventCard(data, index, rowIndex, h, w, style, type, totalCards)
  }
}

export const createPaginationCard = (data, index, rowIndex, h, w, totalCards) => {
  return {
    h: h,
    w: w,
    type: TvPaginationCard,
    ref: toCardRef(index),
    data: data,
    rowIndex: rowIndex,
    cardIndex: index,
    totalCards: totalCards,
  }
}

export const createEventCard = (data, index, rowIndex, h, w, style, type, totalCards) => {
  return {
    h: h,
    w: w,
    type: TvEventCard,
    ref: toCardRef(index),
    data: data,
    style: style,
    rowIndex: rowIndex,
    cardIndex: index,
    cardType: type,
    totalCards: totalCards,
  }
}

export const createVideoCard = (data, index, rowIndex, h, w, style, type, totalCards) => {
  return {
    h: h,
    w: w,
    type: TvVideoCard,
    ref: toCardRef(index),
    data: data,
    style: style,
    rowIndex: rowIndex,
    cardIndex: index,
    cardType: type,
    totalCards: totalCards,
  }
}

export const createNavigationCard = (data, index, rowIndex, h, w, style, type, totalCards) => {
  return {
    h: h,
    w: w,
    type: TvNavigationCard,
    ref: toCardRef(index),
    data: data,
    style: style,
    rowIndex: rowIndex,
    cardIndex: index,
    cardType: type,
    totalCards: totalCards,
  }
}

export const createOriginalCard = (data, index, rowIndex, h, w, style, type, totalCards) => {
  return {
    h: h,
    w: w,
    type: TvOriginalCard,
    ref: toCardRef(index),
    data: data,
    style: style,
    rowIndex: rowIndex,
    cardIndex: index,
    cardType: type,
    totalCards: totalCards,
  }
}

export const createEntityCard = (data, index, rowIndex, h, w, style, type, totalCards) => {
  return {
    h: h,
    w: w,
    type: TvEntityCard,
    ref: toCardRef(index),
    data: data,
    style: style,
    rowIndex: rowIndex,
    cardIndex: index,
    cardType: type,
    totalCards: totalCards,
  }
}

export const createFavoriteCard = (data, index, rowIndex, style, type, totalCards) => {
  return {
    type: TvFavoriteCard,
    ref: toCardRef(index),
    data: data,
    style: style,
    rowIndex: rowIndex,
    cardIndex: index,
    cardType: type,
    totalCards: totalCards,
  }
}

export const createBannerMessage = (data, index, rowIndex, h, w) => {
  return {
    h: h,
    w: w,
    type: TvBannerMessage,
    ref: toCardRef(index),
    data: data,
    rowIndex: rowIndex,
    cardIndex: index,
  }
}

export const createButton = (data, index, rowIndex, totalCards) => {
  return {
    type: data?.style === TvButtonStylesEnum.EditFavorites ? EditFavoritesButton : Button,
    ref: toCardRef(index),
    data: data,
    adjustWidthToContent: true,
    rowIndex: rowIndex,
    buttonIndex: index,
    totalCards: totalCards,
    flexItem: {
      marginRight: CARD_SPACING,
    },
  }
}

export const toContainerItem = (data, index, rowIndex, h, w, style) => {
  const type = data?.type

  if (type === MessageModelTypesEnum.Message) {
    return toMessage(data, index, rowIndex, h, style)
  } else {
    return toCard(data, index, rowIndex, h, w, style, 0)
  }
}

export const toMessage = (data, index, rowIndex, h, style, isCarousel = false) => {
  return {
    h: h,
    type: Message,
    ref: isCarousel ? toCarouselRef(rowIndex ?? 0) : toCardRef(index),
    data: data,
    style: style,
    rowIndex: rowIndex,
    isCarousel: isCarousel,
  }
}

export const getItemSize = (
  type: TvCardType | MessageModelType | LinkModelType,
  style: TvCardStyle | TvButtonStyle | MessageModelStyle | LinkModelStyle,
) => {
  if (type === MessageModelTypesEnum.Message) {
    return getMessageSize(style as MessageModelStyle)
  } else {
    return getCardSize(type as TvCardType, style as TvCardStyle)
  }
}

export const getMessageSize = (style: MessageModelStyle = MessageModelStylesEnum.NoContent) => {
  const messageSizes = {
    [MessageModelStylesEnum.Alternate]: alternateMessageSize,
    [MessageModelStylesEnum.Info]: messageSize,
    [MessageModelStylesEnum.NoContent]: messageSize,
    [MessageModelStylesEnum.NoResultsForDates]: messageSize,
    [MessageModelStylesEnum.SearchNoResults]: messageSize,
  }

  return messageSizes[style]
}

const eventCardSize = {
  large: {
    w: 528,
    h: 505,
  },
  medium: {
    w: 528,
    h: 297,
  },
  small: {
    w: 388,
    h: 403,
  },
}

const videoCardSize = {
  large: {
    w: 528,
    h: 505,
  },
  medium: {
    w: 528,
    h: 297,
  },
  small: {
    w: 388,
    h: 403,
  },
}

const navigationCardSize = {
  standard: {
    w: 388,
    h: 260,
  },
}

const originalCardSize = {
  large: {
    w: 388,
    h: 736,
  },
  standard: {
    w: 388,
    h: 736,
  },
}

const entityCardSize = {
  large: {
    w: 388,
    h: 544,
  },
  standard: {
    w: 388,
    h: 544,
  },
}

const favoriteCardSize = {
  standard: { w: 388, h: 120 },
}

const carouselButtonSize = {
  standard: {
    w: 388, // Default width, is calculated in the component as it is dynamic
    h: 84,
  },
}

const bannerCardSize = {
  standard: {
    w: 1648,
    h: 220,
  },
}

const messageSize = {
  h: 180,
}

const alternateMessageSize = {
  h: 229,
}
